/**
 * Admin App — authentication shell + navigation.
 *
 * This file is the deployment entry point only.
 * All admin UI panels are imported from the existing web/src/components/admin/
 * via the @web alias (resolved to ../web/src in vite.config.ts).
 *
 * Auth mechanism: on login success the JWT is stored in localStorage as
 * 'eurion_token'. A fetch interceptor is installed globally so that all
 * relative API calls made by the admin components (/v1/... and /api/...)
 * automatically carry the Authorization header.
 */
import { useState, useEffect } from 'react';
import { installAuthInterceptor, saveTokens, clearTokens, takeExpiredFlag } from './authFetch';
import {
  LayoutDashboard, Users, Workflow, Smartphone,
  Globe, KeySquare, LogOut, Shield, Fingerprint,
  Building2, Server, ScrollText, Settings, Download, RefreshCw, KeyRound, AlertTriangle, Inbox,
} from 'lucide-react';

// ── Existing admin UI panels (no new UI code) ─────────────────────────────
import { AdminView, DashboardPanel, OrgsPanel, UsersPanel, DomainsPanel, ServicesPanel, AuditPanel, SettingsPanel } from '@web/components/admin/AdminView';
import { ApprovalInbox }     from '@web/components/admin/ApprovalInbox';
import { WorkflowBuilder }   from '@web/components/admin/WorkflowBuilder';
import { DeviceManager }     from '@web/components/admin/DeviceManager';
import { FederationManager } from '@web/components/admin/FederationManager';
import { ApiAppsManager }    from '@web/components/admin/ApiAppsManager';
import { WhiteLabelManager } from '@web/components/admin/WhiteLabelManager';
import { DirectorySync }     from '@web/components/admin/DirectorySync';
import { ssoLoginPath }      from '@web/lib/ssoLogin';
import IdentityProviders     from './IdentityProviders';
import SystemUpdates, { useUpdateAvailable } from './SystemUpdates';
import SupportMenu           from './SupportMenu';
import LicensePage, { useLicenseStatus, bannerText } from './License';
import SharedMailboxes       from './SharedMailboxes';

// ── Types ─────────────────────────────────────────────────────────────────
type AdminPanel =
  | 'dashboard' | 'identity-providers' | 'directory-sync'
  | 'orgs' | 'users' | 'domains' | 'shared-mailboxes'
  | 'services' | 'audit' | 'settings'
  | 'approvals' | 'workflows' | 'devices'
  | 'federation' | 'api-apps' | 'whitelabel' | 'updates' | 'license';

interface User {
  id: string;
  email: string;
  displayName: string;
  orgId: string;
  roles: string[];
}

function uninstallAuthInterceptor() {
  // Reload to restore a clean state on logout
  clearTokens();
  window.location.reload();
}

// ── SSO types ─────────────────────────────────────────────────────────────
interface SsoProvider {
  key: string;
  label: string;
  enabled: boolean;
  type: string;
}

function MicrosoftIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function SamlIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  );
}

// ── Login screen ──────────────────────────────────────────────────────────
function LoginScreen({ onLogin }: { onLogin: (user: User, token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // Shown once after the session expired and could not be renewed (authFetch.ts)
  const [expired] = useState(() => takeExpiredFlag());
  const [loading, setLoading] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([]);

  // ── Handle OAuth callback (tokens in URL after SSO redirect) ──
  useEffect(() => {
    // Tokens come back in the fragment (identity sso-return.ts); older servers used the query
    const fromHash = new URLSearchParams(window.location.hash.slice(1));
    const params = fromHash.has('access_token') ? fromHash : new URLSearchParams(window.location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (accessToken && refreshToken) {
      window.history.replaceState({}, '', window.location.pathname);
      saveTokens(accessToken, refreshToken);
      installAuthInterceptor();

      // Fetch user profile
      (async () => {
        try {
          const res = await fetch('/v1/auth/me', {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const data = await res.json() as { success: boolean; data?: { user: User } };
          if (data.success && data.data) {
            const user = data.data.user;
            if (!user.roles.some(r => r === 'admin' || r === 'super-admin')) {
              setError('Access denied. Admin role required.');
              clearTokens();
              return;
            }
            onLogin(user, accessToken);
          }
        } catch {
          setError('SSO login failed. Please try again.');
        }
      })();
    }
  }, [onLogin]);

  // ── Fetch available SSO providers ──
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/v1/auth/providers');
        const data = await res.json() as { success: boolean; data?: { providers: SsoProvider[] } };
        if (data.success && data.data) {
          // Only providers the admin portal can start a login for (no dead buttons)
          setSsoProviders(data.data.providers.filter(p => p.enabled && ssoLoginPath(p.key, '') !== null));
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const handleSsoLogin = (providerKey: string) => {
    const url = ssoLoginPath(providerKey, window.location.origin);
    if (url) window.location.href = url;
  };

  const getSsoIcon = (key: string) => {
    switch (key) {
      case 'azure': return <MicrosoftIcon />;
      case 'google': return <GoogleIcon />;
      case 'saml': return <SamlIcon />;
      default: return null;
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json() as {
        success: boolean;
        // identity-service answers { user, tokens: { accessToken, … } }
        data?: { tokens?: { accessToken: string; refreshToken?: string }; accessToken?: string; refreshToken?: string; user: User };
        error?: { message: string };
      };
      const accessToken = data.data?.tokens?.accessToken ?? data.data?.accessToken;
      if (data.success && data.data && accessToken) {
        const { user } = data.data;
        if (!user.roles.some(r => r === 'admin' || r === 'super-admin')) {
          setError('Access denied. Admin role required.');
          return;
        }
        saveTokens(accessToken, data.data.tokens?.refreshToken ?? data.data.refreshToken);
        installAuthInterceptor();
        onLogin(user, accessToken);
      } else {
        setError(data.error?.message ?? 'Invalid credentials');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-nord-0">
      <div className="w-full max-w-sm rounded-2xl border border-nord-2 bg-nord-1 p-8 shadow-xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center">
            <img src={`${import.meta.env.BASE_URL}logo-mark.svg`} alt="Eurion" className="h-12 w-12" />
          </div>
          <h1 className="text-xl font-semibold text-nord-6">Eurion Admin</h1>
          <p className="mt-1 text-sm text-nord-4">Administration console</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-nord-4" htmlFor="email">Email</label>
            <input id="email" type="email" required autoComplete="email"
              value={email} onChange={e => setEmail(e.target.value)}
              className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-6 focus:border-nord-8 focus:outline-none"
              placeholder="admin@organisation.eu" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-nord-4" htmlFor="password">Password</label>
            <input id="password" type="password" required autoComplete="current-password"
              value={password} onChange={e => setPassword(e.target.value)}
              className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-6 focus:border-nord-8 focus:outline-none"
              placeholder="••••••••" />
          </div>
          {expired && !error && <p role="status" className="rounded-lg bg-nord-13/10 px-3 py-2 text-sm text-nord-13">Your session has expired. Please sign in again.</p>}
          {error && <p className="rounded-lg bg-nord-11/10 px-3 py-2 text-sm text-nord-11">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full rounded-lg bg-nord-10 py-2.5 text-sm font-semibold text-nord-6 transition hover:opacity-90 disabled:opacity-50">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          {/* ── SSO Providers ── */}
          {ssoProviders.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-nord-3" />
                <span className="text-xs text-nord-3">or continue with</span>
                <div className="h-px flex-1 bg-nord-3" />
              </div>
              {ssoProviders.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handleSsoLogin(p.key)}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-nord-3 bg-nord-0 py-2.5 text-sm font-medium text-nord-6 transition hover:border-nord-4 hover:bg-nord-2"
                >
                  {getSsoIcon(p.key)}
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </form>
        <p className="mt-6 text-center text-xs text-nord-3">Admin access only · EURION Platform</p>
      </div>
    </div>
  );
}

// ── Navigation sidebar ────────────────────────────────────────────────────
const NAV: { id: AdminPanel; label: string; icon: typeof LayoutDashboard; section?: string }[] = [
  // ── Overview ──
  { id: 'dashboard',          label: 'Dashboard',           icon: LayoutDashboard, section: 'Overview' },
  // ── Identity & Access ──
  { id: 'identity-providers', label: 'Identity Providers',  icon: Fingerprint,     section: 'Identity & Access' },
  { id: 'directory-sync',     label: 'Directory Sync (SCIM)', icon: RefreshCw },
  { id: 'users',              label: 'Users',               icon: Users },
  { id: 'shared-mailboxes',   label: 'Shared mailboxes',    icon: Inbox },
  { id: 'orgs',               label: 'Organizations',       icon: Building2 },
  { id: 'domains',            label: 'Domains',             icon: Globe },
  // ── Platform ──
  { id: 'services',           label: 'Services',            icon: Server,          section: 'Platform' },
  { id: 'approvals',          label: 'Approvals',           icon: Users },
  { id: 'workflows',          label: 'Workflows',           icon: Workflow },
  { id: 'devices',            label: 'Devices',             icon: Smartphone },
  { id: 'federation',         label: 'Federation',          icon: Globe },
  { id: 'api-apps',           label: 'API Apps',            icon: KeySquare },
  // ── Configuration ──
  { id: 'whitelabel',         label: 'White Label',         icon: Shield,          section: 'Configuration' },
  { id: 'settings',           label: 'Security Policy',     icon: Settings },
  { id: 'audit',              label: 'Audit Logs',          icon: ScrollText },
  { id: 'updates',            label: 'System Updates',      icon: Download },
  { id: 'license',            label: 'License',             icon: KeyRound },
];

// ── Main app ──────────────────────────────────────────────────────────────
export function App() {
  const [user, setUser]     = useState<User | null>(null);
  const [panel, setPanel]   = useState<AdminPanel>('dashboard');
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = !!user?.roles.some(r => r === 'super-admin' || r === 'super_admin');
  const updateVersion = useUpdateAvailable(!!user);
  const licenseStatus = useLicenseStatus(!!user);
  const licenseBanner = licenseStatus ? bannerText(licenseStatus) : null;
  // Shared mailboxes are part of Eurion Mail: hidden when the license never included mail
  const mailHidden = !!licenseStatus?.access?.hidden.includes('mail');

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('eurion_token');
    if (!token) { setLoading(false); return; }
    installAuthInterceptor();
    fetch('/v1/auth/me')
      .then(r => r.json() as Promise<{ success: boolean; data?: User | { user: User } }>)
      .then(d => {
        // identity-service answers { user }; accept a bare user too
        const me = d.data && 'user' in d.data ? d.data.user : d.data;
        if (d.success && me && me.roles.some(r => r === 'admin' || r === 'super-admin')) {
          setUser(me);
        } else {
          clearTokens();
        }
      })
      .catch(() => localStorage.removeItem('eurion_token'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-nord-0">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-nord-8 border-t-transparent" />
      </div>
    );
  }

  if (!user) return <LoginScreen onLogin={(u) => setUser(u)} />;

  const renderPanel = () => {
    switch (panel) {
      case 'dashboard':          return <DashboardPanel />;
      case 'identity-providers': return <IdentityProviders />;
      case 'directory-sync':     return <DirectorySync />;
      case 'orgs':               return <OrgsPanel />;
      case 'users':              return <UsersPanel />;
      case 'shared-mailboxes':   return mailHidden ? null : <SharedMailboxes />;
      case 'domains':            return <DomainsPanel />;
      case 'services':           return <ServicesPanel />;
      case 'audit':              return <AuditPanel />;
      case 'settings':           return <SettingsPanel />;
      case 'approvals':          return <ApprovalInbox />;
      case 'workflows':          return <WorkflowBuilder />;
      case 'devices':            return <DeviceManager />;
      case 'federation':         return <FederationManager />;
      case 'api-apps':           return <ApiAppsManager />;
      case 'whitelabel':         return <WhiteLabelManager orgId={user.orgId} />;
      case 'updates':            return <SystemUpdates isSuperAdmin={isSuperAdmin} />;
      case 'license':            return <LicensePage isSuperAdmin={isSuperAdmin} />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-nord-0">
      {/* Sidebar */}
      <nav className="flex w-56 flex-col border-r border-nord-2 bg-nord-1">
        <div className="flex h-14 items-center gap-2 border-b border-nord-2 px-4">
          <img src={`${import.meta.env.BASE_URL}logo-mark.svg`} alt="Eurion" className="h-6 w-6" />
          <span className="text-sm font-semibold text-nord-6">Eurion Admin</span>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {NAV.filter(({ id }) => !(id === 'shared-mailboxes' && mailHidden)).map(({ id, label, icon: Icon, section }) => (
            <div key={id}>
              {section && (
                <div className="mt-4 mb-1 px-4 first:mt-0">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-nord-3">{section}</span>
                </div>
              )}
              <button onClick={() => setPanel(id)}
                className={`flex w-full items-center gap-2.5 px-4 py-2 text-sm transition ${
                  panel === id ? 'bg-nord-2 text-nord-6' : 'text-nord-4 hover:bg-nord-2/60'
                }`}>
                <Icon className="h-4 w-4 flex-shrink-0" />
                {label}
                {id === 'license' && licenseBanner && (
                  <span title="License needs attention" className={`ml-auto h-2 w-2 rounded-full ${licenseBanner.tone === 'bad' ? 'bg-nord-11' : 'bg-nord-13'}`} />
                )}
                {id === 'updates' && updateVersion && (
                  <span title={`Update ${updateVersion} available`} className="ml-auto h-2 w-2 rounded-full bg-nord-8 shadow-[0_0_6px_#88C0D0]" />
                )}
              </button>
            </div>
          ))}
        </div>
        <div className="border-t border-nord-2 p-3">
          <div className="mb-2 px-1 text-xs text-nord-3 truncate">{user.email}</div>
          <button onClick={uninstallAuthInterceptor}
            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-nord-4 hover:bg-nord-2">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </nav>
      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 flex-shrink-0 items-center justify-end border-b border-nord-2 px-4">
          <SupportMenu />
        </header>
        <main className="flex-1 overflow-auto">
          {licenseBanner && panel !== 'license' && (
            <button onClick={() => setPanel('license')}
              className={`flex w-full items-center gap-2 border-b px-6 py-2 text-left text-sm text-nord-5 ${licenseBanner.tone === 'bad' ? 'border-nord-11/40 bg-nord-11/10' : 'border-nord-13/40 bg-nord-13/10'}`}>
              <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${licenseBanner.tone === 'bad' ? 'text-nord-11' : 'text-nord-13'}`} />
              {licenseBanner.text}
            </button>
          )}
          {renderPanel()}
        </main>
      </div>
    </div>
  );
}
